<script setup lang="ts">
import { ref, watch, inject, Ref } from 'vue'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Member } from '@/types/Member'
import useReports from '@/services/Reports'
import vhostService from '@/services/Vhost'
import BarChart from '@/components/BarChart.vue'

const {
	uploadData,
	viewData,
	bandwidthData,
	loading,
	getUploadReport,
	getViewReport,
	getBandwidthReport,
	getMemberReport,
} = useReports()

const { totals, summary, userSummary, getContentSummary, getUserSummary, getTotals } = vhostService()

const vhostId = inject<Ref<string>>('vhostId', ref(''))
const userInfo = inject<Ref<Member> | null>('userInfo', null)

const selectedTab = ref('uploads')
const selectedPeriod = ref('30days')

const formatNumber = (num: number | null): string => {
	if (num === null || num === undefined) {
		return '0'
	}

	return new Intl.NumberFormat('en-US').format(num)
}

const getDateRange = (period: string) => {
	const end = new Date().toISOString().split('T')[0]
	let start = ''

	if (period === '7days') {
		const d = new Date()
		d.setDate(d.getDate() - 7)
		start = d.toISOString().split('T')[0]
	} else if (period === '30days') {
		const d = new Date()
		d.setDate(d.getDate() - 30)
		start = d.toISOString().split('T')[0]
	} else if (period === '90days') {
		const d = new Date()
		d.setDate(d.getDate() - 90)
		start = d.toISOString().split('T')[0]
	} else if (period === '1year') {
		const d = new Date()
		d.setFullYear(d.getFullYear() - 1)
		start = d.toISOString().split('T')[0]
	}

	return { startDate: start, endDate: end }
}

const refreshReports = async () => {
	if (!vhostId.value) {
		return
	}

	const range = getDateRange(selectedPeriod.value)

	await Promise.all([
		getUploadReport(vhostId.value, range),
		getViewReport(vhostId.value, range),
		getBandwidthReport(vhostId.value, range),
		getMemberReport(vhostId.value, range),
		getContentSummary({ vhostId: parseInt(vhostId.value) }),
		getUserSummary({ vhostId: parseInt(vhostId.value) }),
		getTotals(parseInt(vhostId.value)),
	])
}

watch(selectedPeriod, () => {
	refreshReports()
})

watch(vhostId, (newVhostId) => {
	if (newVhostId) {
		refreshReports()
	}
}, { immediate: true })
</script>

<template>
	<div class="p-4 space-y-4">
		<div class="flex justify-between items-center">
			<div>
				<h2 class="text-2xl font-light">Reports & Analytics</h2>
				<p class="text-sm text-muted-foreground">View detailed analytics about your platform usage</p>
			</div>
			<div class="flex items-center space-x-3">
				<Select v-model="selectedPeriod">
					<SelectTrigger class="w-40">
						<SelectValue placeholder="Select period" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="7days">Last 7 Days</SelectItem>
						<SelectItem value="30days">Last 30 Days</SelectItem>
						<SelectItem value="90days">Last 90 Days</SelectItem>
						<SelectItem value="1year">Last Year</SelectItem>
					</SelectContent>
				</Select>
				<Button variant="outline" @click="refreshReports">Refresh</Button>
			</div>
		</div>

		<div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Uploads</CardTitle>
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold" v-if="summary">{{ formatNumber(summary.FileCounts?.Total) }}</div>
					<div class="text-2xl font-bold" v-else>-</div>
					<p class="text-xs text-muted-foreground">Files uploaded in period</p>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Views</CardTitle>
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold" v-if="summary">{{ formatNumber(summary.Views?.Total) }}</div>
					<div class="text-2xl font-bold" v-else>-</div>
					<p class="text-xs text-muted-foreground">Total views in period</p>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Members</CardTitle>
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold" v-if="userSummary">+{{ formatNumber(userSummary.NewUsers) }}</div>
					<div class="text-2xl font-bold" v-else>-</div>
					<p class="text-xs text-muted-foreground">New users in period</p>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Transfers</CardTitle>
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold" v-if="summary">{{ formatNumber(parseInt(summary.Transfers || '0')) }}</div>
					<div class="text-2xl font-bold" v-else>-</div>
					<p class="text-xs text-muted-foreground">Files transferred in period</p>
				</CardContent>
			</Card>
		</div>

		<Tabs v-model="selectedTab" class="space-y-4">
			<!-- Mobile: Select dropdown -->
			<div class="md:hidden">
				<Select :model-value="selectedTab" @update:model-value="(v: string) => selectedTab = v">
					<SelectTrigger>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="uploads">Uploads</SelectItem>
						<SelectItem value="views">Views</SelectItem>
						<SelectItem value="bandwidth">Bandwidth</SelectItem>
						<SelectItem value="members">Members</SelectItem>
						<SelectItem value="content">Content Breakdown</SelectItem>
					</SelectContent>
				</Select>
			</div>
			<!-- Desktop: TabsList -->
			<TabsList class="hidden md:inline-flex">
				<TabsTrigger value="uploads">Uploads</TabsTrigger>
				<TabsTrigger value="views">Views</TabsTrigger>
				<TabsTrigger value="bandwidth">Bandwidth</TabsTrigger>
				<TabsTrigger value="members">Members</TabsTrigger>
				<TabsTrigger value="content">Content Breakdown</TabsTrigger>
			</TabsList>

			<TabsContent value="uploads">
				<Card>
					<CardHeader>
						<CardTitle class="text-lg font-normal">Upload Activity</CardTitle>
						<CardDescription>Number of files uploaded over time</CardDescription>
					</CardHeader>
					<CardContent>
						<div v-if="uploadData.length > 0">
							<BarChart :data="uploadData.map((d) => ({ name: d.label || d.date, total: d.value }))" height="400px" />
						</div>
						<div v-else-if="loading" class="h-[400px] flex items-center justify-center text-muted-foreground">Loading upload data...</div>
						<div v-else class="h-[400px] flex items-center justify-center text-muted-foreground">No upload data available for the selected period.</div>
					</CardContent>
				</Card>
			</TabsContent>

			<TabsContent value="views">
				<Card>
					<CardHeader>
						<CardTitle class="text-lg font-normal">View Activity</CardTitle>
						<CardDescription>Media views over time</CardDescription>
					</CardHeader>
					<CardContent>
						<div v-if="viewData.length > 0">
							<BarChart :data="viewData.map((d) => ({ name: d.label || d.date, total: d.value }))" height="400px" />
						</div>
						<div v-else-if="loading" class="h-[400px] flex items-center justify-center text-muted-foreground">Loading view data...</div>
						<div v-else class="h-[400px] flex items-center justify-center text-muted-foreground">No view data available for the selected period.</div>
					</CardContent>
				</Card>
			</TabsContent>

			<TabsContent value="bandwidth">
				<Card>
					<CardHeader>
						<CardTitle class="text-lg font-normal">Bandwidth Usage</CardTitle>
						<CardDescription>Data transfer over time</CardDescription>
					</CardHeader>
					<CardContent>
						<div v-if="bandwidthData.length > 0">
							<BarChart :data="bandwidthData.map((d) => ({ name: d.label || d.date, total: d.value }))" height="400px" />
						</div>
						<div v-else-if="loading" class="h-[400px] flex items-center justify-center text-muted-foreground">Loading bandwidth data...</div>
						<div v-else class="h-[400px] flex items-center justify-center text-muted-foreground">No bandwidth data available for the selected period.</div>
					</CardContent>
				</Card>
			</TabsContent>

			<TabsContent value="members">
				<Card>
					<CardHeader>
						<CardTitle class="text-lg font-normal">Member Statistics</CardTitle>
						<CardDescription>User registration and activity overview</CardDescription>
					</CardHeader>
					<CardContent>
						<div v-if="userSummary" class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
							<Card>
								<CardContent class="pt-6">
									<div class="text-2xl font-bold">{{ formatNumber(userSummary.ActiveUsers) }}</div>
									<p class="text-xs text-muted-foreground">Active Users</p>
								</CardContent>
							</Card>
							<Card>
								<CardContent class="pt-6">
									<div class="text-2xl font-bold">{{ formatNumber(userSummary.RegisteredUsers) }}</div>
									<p class="text-xs text-muted-foreground">Registered Users</p>
								</CardContent>
							</Card>
							<Card>
								<CardContent class="pt-6">
									<div class="text-2xl font-bold">{{ formatNumber(userSummary.NewUsers) }}</div>
									<p class="text-xs text-muted-foreground">New Users</p>
								</CardContent>
							</Card>
							<Card>
								<CardContent class="pt-6">
									<div class="text-2xl font-bold">{{ formatNumber(userSummary.CountOptin) }}</div>
									<p class="text-xs text-muted-foreground">Opted In</p>
								</CardContent>
							</Card>
						</div>
						<div v-else-if="loading" class="h-[200px] flex items-center justify-center text-muted-foreground">Loading member data...</div>
						<div v-else class="h-[200px] flex items-center justify-center text-muted-foreground">No member data available.</div>
					</CardContent>
				</Card>
			</TabsContent>

			<TabsContent value="content">
				<Card>
					<CardHeader>
						<CardTitle class="text-lg font-normal">Content Breakdown</CardTitle>
						<CardDescription>Uploads, views, and activity by media type</CardDescription>
					</CardHeader>
					<CardContent>
						<div v-if="summary" class="space-y-6">
							<div>
								<h3 class="text-sm font-medium mb-3">Uploads by Type</h3>
								<div class="grid gap-3 md:grid-cols-4">
									<div class="flex items-center justify-between p-3 bg-muted/50 rounded">
										<span class="text-sm">Images</span>
										<span class="font-bold">{{ formatNumber(summary.FileCounts?.Image) }}</span>
									</div>
									<div class="flex items-center justify-between p-3 bg-muted/50 rounded">
										<span class="text-sm">Videos</span>
										<span class="font-bold">{{ formatNumber(summary.FileCounts?.Video) }}</span>
									</div>
									<div class="flex items-center justify-between p-3 bg-muted/50 rounded">
										<span class="text-sm">Audio</span>
										<span class="font-bold">{{ formatNumber(summary.FileCounts?.Audio) }}</span>
									</div>
									<div class="flex items-center justify-between p-3 bg-muted/50 rounded">
										<span class="text-sm">Text</span>
										<span class="font-bold">{{ formatNumber(summary.FileCounts?.Text) }}</span>
									</div>
								</div>
							</div>
							<div>
								<h3 class="text-sm font-medium mb-3">Views by Type</h3>
								<div class="grid gap-3 md:grid-cols-4">
									<div class="flex items-center justify-between p-3 bg-muted/50 rounded">
										<span class="text-sm">Images</span>
										<span class="font-bold">{{ formatNumber(summary.Views?.Image) }}</span>
									</div>
									<div class="flex items-center justify-between p-3 bg-muted/50 rounded">
										<span class="text-sm">Videos</span>
										<span class="font-bold">{{ formatNumber(summary.Views?.Video) }}</span>
									</div>
									<div class="flex items-center justify-between p-3 bg-muted/50 rounded">
										<span class="text-sm">Audio</span>
										<span class="font-bold">{{ formatNumber(summary.Views?.Audio) }}</span>
									</div>
									<div class="flex items-center justify-between p-3 bg-muted/50 rounded">
										<span class="text-sm">Text</span>
										<span class="font-bold">{{ formatNumber(summary.Views?.Text) }}</span>
									</div>
								</div>
							</div>
						</div>
						<div v-else-if="loading" class="h-[200px] flex items-center justify-center text-muted-foreground">Loading content data...</div>
						<div v-else class="h-[200px] flex items-center justify-center text-muted-foreground">No content data available.</div>
					</CardContent>
				</Card>
			</TabsContent>
		</Tabs>
	</div>
</template>
